import { AfterViewInit, Component, ElementRef, HostBinding, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { GaiAssistantService } from './gai-assistant.service';
import { filter, interval, mergeMap, Observable, Subject, Subscription, switchMap, takeUntil, tap } from 'rxjs';
import { AppResizeService } from '../utilits/app-resize.service';
import { PerfectScrollbarComponent, PerfectScrollbarConfigInterface, PerfectScrollbarDirective } from 'ngx-om-perfect-scrollbar';
import { LocalStorageService } from '@vantage-platform/store/lib/utilities/local-storage.service';
import { AuthService } from '@vantage-platform/auth';
import { APPS, AppState, getCurrentApp, IInstance, assistantEnabled, IUser } from '@vantage-platform/store';
import { DeviceDetectorService } from '../utilits/device-detector.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

interface Message {
  text: string;
  user: boolean;
}

export const GAI_ASSISTANT_KEY_PART = 'gai-assistant';
@Component({
  selector: 'vp-gai-assistant',
  templateUrl: './gai-assistant.component.html',
  styleUrls: ['./gai-assistant.component.scss']
})
export class GaiAssistantComponent implements OnInit, AfterViewInit {
  @Input() user: Observable<IUser>;
  @Input() instance: Observable<IInstance>;

  @ViewChild('messagesWrapper', { static: true }) messagesContainer!: ElementRef;  

  message = '';
  messages: Message[] = [];
  typing = false;
  instanceConfig: { [key: string]: any };
  notAvailableInApp = false;
  availabilityCheckDone = false;
  assistantEnabledValue = false;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  botConfig: { [key: string]: any, 'AIAssistantEnabled': string };
  @HostBinding('class.active') active = false;
  @HostBinding('class.full-window') fullWindow = false;
  @HostBinding('class.visible') get visible() { 
    return this.availabilityCheckDone && this.assistantEnabledValue && !this.notAvailableInApp;
  };

  public config: PerfectScrollbarConfigInterface = {};
  lsKey = GAI_ASSISTANT_KEY_PART;
  profile;
  
  _user: IUser;
  _instance: IInstance;

  @ViewChild(PerfectScrollbarComponent, {static: true}) scrollbar?: PerfectScrollbarComponent;
  @ViewChild(PerfectScrollbarDirective, { static: false }) directiveRef?: PerfectScrollbarDirective;
  destroy$ = new Subject<void>();
  watchSubscribe: Subscription;

  @HostListener('window:resize', ['$event']) onResize(event) {
    // set flag to open bot for mobile/tablet for a full screen view
    if (this.deviceDetector.isMobile() || this.deviceDetector.isTablet()) {
      this.fullWindow = true;
    }
  }


  constructor(private assistantService: GaiAssistantService, public appResize: AppResizeService,
    private localStorage: LocalStorageService, private auth: AuthService, public deviceDetector: DeviceDetectorService,
    private store: Store<AppState>, private translate: TranslateService
  ){
    this.store.select(assistantEnabled).pipe(takeUntil(this.destroy$)).subscribe(enabled => {
      this.assistantEnabledValue = enabled;
    });
  }

  ngOnInit() {
    // set flag to open bot for mobile/tablet for a full screen view
    if (this.deviceDetector.isMobile() || this.deviceDetector.isTablet()) {
      this.fullWindow = true;
    }

    this.user.pipe(takeUntil(this.destroy$)).subscribe(user => {
      this._user = user;
    })

    this.instance.pipe(
      takeUntil(this.destroy$),
      filter(u => u && u.id !== this._instance?.id),
      tap(instance => {
        this._instance = instance;
        this.availabilityCheckDone = false;
      }),
      switchMap((instance) => this.assistantService.checkAssistantAvailability(instance.id))
    ).subscribe((config) => {
      this.instanceConfig = config
      this.close();
      this.availabilityCheckDone = true;
      if(config !== null) {
        this.botConfig = JSON.parse(config['value'])
        this._initBot(this._instance.id);
      }
    });

    this.store.select(getCurrentApp).pipe(takeUntil(this.destroy$)).subscribe((app) => {
      if(this.instanceConfig) {
        this.notAvailableInApp = app?.name?.toLowerCase() === APPS.submission;
      }
    })

    if (this.auth.isLoggedIn) {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { family_name, given_name } = this.auth.user
        .idTokenClaims as any;
      const initials = `${given_name.substring(0, 1)}${family_name.substring(0, 1)}`.toUpperCase();
      this.profile = {
        initials
      };
    }
  }

  ngAfterViewInit(): void {
    this._scrollToBot();
  }

  ngOnDestroy() {
    this.destroy$.next();
  }

  /**
   * sent message to the bot
   * watch for the response every 3 seconds
   * until watching - user can not sent message again
   */
  sendMessage() {
    if (!this.typing && this.message.trim()) {
      
      this.messages.push({ text: this.message, user: true });
      this.localStorage.setItem(this.lsKey, this.messages);
      this._scrollToBot();
      this.assistantService.postMessage(this._instance.id, this._user.id, encodeURIComponent(this.message)).subscribe(res => {
        this.typing = true;
        this._scrollToBot();
        const done = new Subject();
        interval(3000).pipe(
          mergeMap(() => this.assistantService.getMessage(this._instance.id, this._user.id, res['taskId']))
        ).pipe(takeUntil(done)).subscribe(ans => {
          if(ans["done"]) {
            this.typing = false;
            done.next(true);
            done.complete();
            this.messages.push({ text: ans['response'], user: false });
            this.localStorage.setItem(this.lsKey, this.messages);
            this._scrollToBot(true);
          }
        })
      
        this.message = '';
      });
    }
  }

  close() {
    this.active = false;
    this.appResize.xpoint = window.innerWidth;
    this.appResize.enabled = false;
  }

  fullscreen() {
    this.fullWindow = !this.fullWindow;
  }

  toggleAssistant() {
    this.active = true;
    this.appResize.enabled = true;
    this.appResize.xpoint = window.innerWidth - 416;

    this._scrollToBot(true);
  }

  trackByFn(index) {
    return index; // or item.id
  }

  /**
   * re-init bot in case of changing instance
   * @param instanceId 
   */
  private _initBot(instanceId: number) {
    if (this.watchSubscribe) {
      this.watchSubscribe.unsubscribe();
    }
    this.assistantService.apimkey = this.botConfig.AIAssistantEnabled;
    this.lsKey = `${GAI_ASSISTANT_KEY_PART}_${instanceId}`;
    this.messages = this.localStorage.getItem(this.lsKey) || [this.getInitialBotMessage()];
    
    this.watchSubscribe = this.localStorage.watchKey(this.lsKey).subscribe((val) => {
      // if(!val) {
      //   this.messages = [this.initialBotMessage];
      // }
      if(val && JSON.stringify(this.messages) !== JSON.stringify(val)) {
        this.messages = val;
        this._scrollToBot(true);
      } 
    })
  }

  private getInitialBotMessage(): Message {
    return {
      text: this.translate.instant('assistant.initialBotMessage'),
      user: false
    };
  }

  private _scrollToBot(useLastElement = false) {
    setTimeout(() => {
      if(useLastElement) {
        const botMessageElements  = this.messagesContainer.nativeElement.querySelectorAll('.bot-message');  
        const lastBotMessage = botMessageElements[botMessageElements.length - 1]; 
        if (lastBotMessage) {  
          const userMessageElements = this.messagesContainer.nativeElement.querySelectorAll('.user-message');
          if (userMessageElements.length > 0) {
            this.scrollbar.directiveRef.scrollToTop(userMessageElements[userMessageElements.length - 1].offsetTop, 200);
          }
        }
      } else {
        this.scrollbar.directiveRef.scrollToBottom(0, 200);
      }
    });
  }
}
